import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import { Circle, MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import "../assets/css/expandable_leaflet_map.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function toNumericCoordinate(value) {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMarkers(markers) {
  return (Array.isArray(markers) ? markers : [])
    .map((marker) => {
      const lat = toNumericCoordinate(marker?.lat);
      const lng = toNumericCoordinate(marker?.lng);
      if (lat == null || lng == null) return null;

      return {
        ...marker,
        lat,
        lng,
      };
    })
    .filter(Boolean);
}

function MapViewportController({ markers, selectedMarker, expanded, selectVersion }) {
  const map = useMap();

  useEffect(() => {
    const resizeTimer = window.setTimeout(() => {
      map.invalidateSize();
    }, 0);

    return () => window.clearTimeout(resizeTimer);
  }, [map, expanded, markers.length]);

  useEffect(() => {
    if (!markers.length) return;

    if (selectedMarker) {
      map.setView([selectedMarker.lat, selectedMarker.lng], 15, { animate: true });
      return;
    }

    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 13, { animate: false });
      return;
    }

    const bounds = L.latLngBounds(markers.map((marker) => [marker.lat, marker.lng]));
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 14 });
  }, [map, markers, selectedMarker, selectVersion]);

  if (!selectedMarker) return null;

  return (
    <Circle
      center={[selectedMarker.lat, selectedMarker.lng]}
      radius={150}
      pathOptions={{ color: "#b7791f", fillColor: "#f6ad55", fillOpacity: 0.2, weight: 2 }}
    />
  );
}

export const ExpandableLeafletMap = ({
  markers,
  selectedId,
  selectVersion = 0,
  onSelect,
  emptyTitle = "No mappable locations yet",
  emptyText = "Add or select a location with coordinates to see the map preview.",
}) => {
  const [expanded, setExpanded] = useState(false);

  const validMarkers = useMemo(() => normalizeMarkers(markers), [markers]);
  const selectedMarker = useMemo(
    () => validMarkers.find((marker) => String(marker.id) === String(selectedId)) || null,
    [selectedId, validMarkers]
  );

  useEffect(() => {
    if (!expanded) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalDocumentOverflow = document.documentElement.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.classList.add("elm-modal-open");
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalDocumentOverflow;
      document.body.classList.remove("elm-modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [expanded]);

  const content = (
    <div className={`elm-shell ${expanded ? "is-expanded" : ""}`}>
      {expanded ? (
        <button
          type="button"
          className="elm-backdrop"
          aria-label="Close expanded map"
          onClick={() => setExpanded(false)}
        />
      ) : null}

      <div className="elm-surface" role={expanded ? "dialog" : undefined} aria-modal={expanded || undefined}>
        <div className="elm-toolbar">
          <div className="elm-summary">
            {validMarkers.length === 0
              ? "Map unavailable"
              : selectedMarker
                ? `${selectedMarker.title || "Selected stop"} pinned`
                : `${validMarkers.length} map stop${validMarkers.length === 1 ? "" : "s"} available`}
          </div>

          <button
            type="button"
            className="btn-cta"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "Close map" : "Expand map"}
          </button>
        </div>

        <div className={`elm-frame ${expanded ? "is-expanded" : ""}`}>
          {validMarkers.length === 0 ? (
            <div className="elm-empty">
              <div className="elm-emptyTitle">{emptyTitle}</div>
              <div className="elm-emptyText">{emptyText}</div>
            </div>
          ) : (
            <MapContainer
              center={[validMarkers[0].lat, validMarkers[0].lng]}
              zoom={13}
              scrollWheelZoom
              attributionControl={false}
              className="elm-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapViewportController
                markers={validMarkers}
                selectedMarker={selectedMarker}
                expanded={expanded}
                selectVersion={selectVersion}
              />

              {validMarkers.map((marker) => (
                <Marker
                  key={marker.id}
                  position={[marker.lat, marker.lng]}
                  eventHandlers={{
                    click: () => {
                      if (typeof onSelect === "function") onSelect(marker.id);
                    },
                  }}
                />
              ))}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );

  if (expanded && typeof document !== "undefined") {
    return createPortal(content, document.body);
  }

  return content;
};
